import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Row, Col, Input } from "antd";

import InputItem from "../../app/generic/inputItem";
import SelectItem from "../../app/generic/selectItem";
import IconButton from "../../app/generic/iconButton";
import HiddenItem from "../../app/generic/hiddenItem";
import RadioGroupItem from "../../app/generic/radioGroupItem";
import InputNumberItem from "../../app/generic/inputNumberItem";
import { roundNumber } from "../../../modules/consignmentFunctions";
import SelectItemWithLoad from "../../app/generic/selectItemWithLoad";

import "./index.css";

const logoUrl = `${process.env.PUBLIC_URL}/adr/limited_quantity.png`;

const LimitedQuantityLogo = () => (
  <img src={logoUrl} alt="limited_quantity" className="limited-quantity-logo" />
);

const isBool = (value) => typeof value === "boolean";

const getBoolValue = (initialAdr, prop) =>
  initialAdr && isBool(initialAdr[prop]) ? initialAdr[prop] : false;

const AdrItem = ({
  intl,
  index,
  count,
  disabled,
  maxCount,
  maxWeight,
  unClasses,
  initialAdr,
  onAdrDelete,
  onSetQuantity,
  packagingTypes,
  formItemLayout,
  packagingGroups,
  onSetGrossWeight,
  getFieldDecorator,
  onSetHandlingUnitsQuantity,
}) => {
  const { data } = useSelector((state) => state.language);
  const czech = useMemo(() => data && data.iso6391Code === "cs", [data]);

  const [underLimitQuantity, setUnderLimitQuantity] = useState();
  const [fullAdrQuantity, setFullAdrQuantity] = useState();
  const [limitedQuantity, setLimitedQuantity] = useState();
  const [exemptedQuantity, setExemptedQuantity] = useState();
  const [emptyPackage, setEmptyPackage] = useState();
  const [maxQuantity, setMaxQuantity] = useState();
  const [quantity, setQuantity] = useState();
  const [grossWeight, setGrossWeight] = useState();
  const [handlingUnitsQuantity, setHandlingUnitsQuantity] = useState();
  useEffect(() => {
    setUnderLimitQuantity(
      initialAdr && isBool(initialAdr.underLimitQuantity)
        ? initialAdr.underLimitQuantity
        : true
    );
    setFullAdrQuantity(getBoolValue(initialAdr, "fullAdrQuantity"));
    setLimitedQuantity(getBoolValue(initialAdr, "limitedQuantity"));
    const eq = getBoolValue(initialAdr, "exemptedQuantity");
    setExemptedQuantity(eq);
    setEmptyPackage(getBoolValue(initialAdr, "emptyPackage"));
    setMaxQuantity((eq && initialAdr.grossWeight) || maxWeight);
    setQuantity((initialAdr && initialAdr.quantity) || 0);
    setGrossWeight((initialAdr && initialAdr.grossWeight) || 0);
    setHandlingUnitsQuantity(
      (initialAdr && initialAdr.handlingUnitsQuantity) || 0
    );
  }, [initialAdr, maxWeight]);

  const handleQuantityChange = useCallback(
    (value) => {
      onSetQuantity(roundNumber(value - quantity));
      setQuantity(value);
    },
    [quantity, onSetQuantity]
  );
  const handleGrossWeightChange = useCallback(
    (value) => {
      onSetGrossWeight(roundNumber(value - grossWeight));
      setGrossWeight(value);
    },
    [grossWeight, onSetGrossWeight]
  );
  const handleGrossWeightBlur = useCallback(
    (e) => {
      setMaxQuantity(
        exemptedQuantity ? Number.parseFloat(e.target.value || 0) : maxWeight
      );
    },
    [maxWeight, exemptedQuantity]
  );
  const handleHandlingUnitsQuantityChange = useCallback(
    (value) => {
      onSetHandlingUnitsQuantity(value - handlingUnitsQuantity);
      setHandlingUnitsQuantity(value);
    },
    [handlingUnitsQuantity, onSetHandlingUnitsQuantity]
  );

  const handleAdrDelete = useCallback(() => {
    onAdrDelete(index, [
      quantity * -1,
      grossWeight * -1,
      handlingUnitsQuantity * -1,
    ]);
  }, [index, onAdrDelete, quantity, grossWeight, handlingUnitsQuantity]);

  const handleQuantityTypeChange = useCallback(
    (e) => {
      const underLimit = e.target.value === 1,
        full = e.target.value === 2,
        limited = e.target.value === 3,
        exempted = e.target.value === 4,
        empty = e.target.value === 5;

      setUnderLimitQuantity(underLimit);
      setFullAdrQuantity(full);
      setLimitedQuantity(limited);
      setExemptedQuantity(exempted);
      setEmptyPackage(empty);

      !underLimit && !full && !exempted && handleQuantityChange(0);
      !limited && !exempted && handleGrossWeightChange(0);
      limited && handleHandlingUnitsQuantityChange(0);
    },
    [
      handleQuantityChange,
      handleGrossWeightChange,
      handleHandlingUnitsQuantityChange,
    ]
  );

  useEffect(() => {
    setMaxQuantity(exemptedQuantity ? grossWeight : maxWeight);
  }, [maxWeight, grossWeight, exemptedQuantity]);

  return (
    <Row type="flex" align="middle">
      <Col span={18}>
        <Input.Group>
          <HiddenItem
            fieldName={`adrId[${index}]`}
            getFieldDecorator={getFieldDecorator}
            initialValue={initialAdr && initialAdr.id}
          />
          <SelectItemWithLoad
            options={unClasses}
            errorMessage={intl.formatMessage({
              id: "consignment.services.adr.unClassError",
              defaultMessage:
                "Při načítání UN tříd nastala chyba, opakujte prosím načtení formuláře!",
            })}
          >
            {!limitedQuantity && (
              <SelectItem
                toolTip
                allowClear
                disabled={disabled}
                options={unClasses}
                fieldName={`unClassId[${index}]`}
                formItemLayout={formItemLayout}
                className="services-form-adr-select"
                getFieldDecorator={getFieldDecorator}
                label={intl.formatMessage({
                  id: "consignment.services.adr.unClass",
                  defaultMessage: "Třída nebezpečnosti",
                })}
                message={intl.formatMessage({
                  id: "consignment.services.adr.emptyUnClass",
                  defaultMessage: "Vyberte prosím UN třídu!",
                })}
                initialValue={initialAdr && initialAdr.unClassId}
              />
            )}
          </SelectItemWithLoad>
          {!limitedQuantity && (
            <InputItem
              unCode
              allowClear
              disabled={disabled}
              fieldName={`unCode[${index}]`}
              formItemLayout={formItemLayout}
              getFieldDecorator={getFieldDecorator}
              label={intl.formatMessage({
                id: "consignment.services.adr.unCode",
                defaultMessage: "UN číslo",
              })}
              message={intl.formatMessage({
                id: "consignment.services.adr.emptyUnCode",
                defaultMessage: "Zadejte prosím UN číslo!",
              })}
              initialValue={initialAdr && initialAdr.unCode}
            />
          )}
          {!limitedQuantity && (
            <InputItem
              allowClear
              disabled={disabled}
              formItemLayout={formItemLayout}
              fieldName={`chemicalName[${index}]`}
              getFieldDecorator={getFieldDecorator}
              label={intl.formatMessage({
                id: "consignment.services.adr.chemicalName",
                defaultMessage: "Chemický název",
              })}
              message={intl.formatMessage({
                id: "consignment.services.adr.emptyChemicalName",
                defaultMessage: "Zadejte prosím chemický název!",
              })}
              initialValue={initialAdr && initialAdr.chemicalName}
            />
          )}
          {!limitedQuantity && (
            <SelectItemWithLoad
              options={packagingGroups}
              errorMessage={intl.formatMessage({
                id: "consignment.services.adr.packagingGroupError",
                defaultMessage:
                  "Při načítání obalových skupin nastala chyba, opakujte prosím načtení formuláře!",
              })}
            >
              <SelectItem
                allowClear
                disabled={disabled}
                options={packagingGroups}
                formItemLayout={formItemLayout}
                className="services-form-adr-select"
                getFieldDecorator={getFieldDecorator}
                fieldName={`packagingGroupId[${index}]`}
                label={intl.formatMessage({
                  id: "consignment.services.adr.packagingGroup",
                  defaultMessage: "Obalová skupina",
                })}
                /*message={intl.formatMessage({
                    id: "consignment.services.adr.emptyPackagingGroup",
                    defaultMessage: "Vyberte prosím obalovou skupinu!",
                  })}*/
                initialValue={initialAdr && initialAdr.packagingGroupId}
              />
            </SelectItemWithLoad>
          )}
          {!limitedQuantity && (
            <SelectItemWithLoad
              options={packagingTypes}
              errorMessage={intl.formatMessage({
                id: "consignment.services.adr.packagingTypeError",
                defaultMessage:
                  "Při načítání typů obalů nastala chyba, opakujte prosím načtení formuláře!",
              })}
            >
              <SelectItem
                allowClear
                useNameAndCode
                disabled={disabled}
                options={packagingTypes}
                formItemLayout={formItemLayout}
                className="services-form-adr-select"
                fieldName={`packagingTypeId[${index}]`}
                getFieldDecorator={getFieldDecorator}
                label={intl.formatMessage({
                  id: "consignment.services.adr.packagingType",
                  defaultMessage: "Typ obalu",
                })}
                message={intl.formatMessage({
                  id: "consignment.services.adr.emptyPackagingType",
                  defaultMessage: "Vyberte prosím typ obalu!",
                })}
                initialValue={initialAdr && initialAdr.packagingTypeId}
              />
            </SelectItemWithLoad>
          )}
          {!limitedQuantity && !emptyPackage && (
            <InputNumberItem
              allowClear
              precision={1}
              max={maxQuantity}
              disabled={disabled}
              fieldName={`quantity[${index}]`}
              formItemLayout={formItemLayout}
              onInputChange={handleQuantityChange}
              getFieldDecorator={getFieldDecorator}
              className={
                fullAdrQuantity || underLimitQuantity || !czech
                  ? "services-form-two-line-label"
                  : ""
              }
              label={
                fullAdrQuantity || underLimitQuantity
                  ? intl.formatMessage({
                      id: "consignment.services.adr.quantity.extended",
                      defaultMessage: "Celkové množství ADR látky",
                    })
                  : intl.formatMessage({
                      id: "consignment.services.adr.quantity",
                      defaultMessage: "Množství ADR látky",
                    })
              }
              message={intl.formatMessage({
                id: "consignment.services.adr.emptyQuantity",
                defaultMessage: "Zadejte prosím množství ADR látky!",
              })}
              initialValue={initialAdr && initialAdr.quantity}
            />
          )}
          <RadioGroupItem
            vertical
            disabled={disabled}
            formItemLayout={formItemLayout}
            onChange={handleQuantityTypeChange}
            fieldName={`quantityType[${index}]`}
            className="services-form-adr-quantity"
            label={intl.formatMessage({
              id: "consignment.services.adr.quantityType",
              defaultMessage: "Typ množství",
            })}
            labels={[
              intl.formatMessage({
                id: "consignment.services.adr.underLimitQuantity",
                defaultMessage: "Podlimitní množství dle 1.1.3.6.",
              }),
              intl.formatMessage({
                id: "consignment.services.adr.fullAdrQuantity",
                defaultMessage: "Plná - nadlimitní ADR",
              }),
              <>
                {intl.formatMessage({
                  id: "consignment.services.adr.limitedQuantity",
                  defaultMessage: "Omezené množství dle 3.4. LQ",
                })}
                <LimitedQuantityLogo />
              </>,
              intl.formatMessage({
                id: "consignment.services.adr.exemptedQuantity",
                defaultMessage: "Vyňaté množství dle 3.5 EQ",
              }),
              intl.formatMessage({
                id: "consignment.services.adr.emptyPackage",
                defaultMessage: "Prázdné obaly",
              }),
            ]}
            getFieldDecorator={getFieldDecorator}
            initialValue={
              initialAdr
                ? initialAdr.underLimitQuantity
                  ? 1
                  : initialAdr.fullAdrQuantity
                  ? 2
                  : initialAdr.limitedQuantity
                  ? 3
                  : initialAdr.exemptedQuantity
                  ? 4
                  : initialAdr.emptyPackage
                  ? 5
                  : 1
                : 1
            }
          />
          {!limitedQuantity && (
            <InputNumberItem
              allowClear
              max={maxCount}
              disabled={disabled}
              formItemLayout={formItemLayout}
              getFieldDecorator={getFieldDecorator}
              fieldName={`handlingUnitsQuantity[${index}]`}
              onInputChange={handleHandlingUnitsQuantityChange}
              className={czech ? "services-form-two-line-label" : ""}
              label={intl.formatMessage({
                id: "consignment.services.adr.handlingUnitsQuantity",
                defaultMessage: "Počet manipulačních jednotek",
              })}
              message={intl.formatMessage({
                id: "consignment.services.adr.emptyHandlingUnitsQuantity",
                defaultMessage: "Zadejte prosím počet manipulačních jednotek!",
              })}
              initialValue={initialAdr && initialAdr.handlingUnitsQuantity}
            />
          )}
          {(limitedQuantity || exemptedQuantity) && (
            <InputNumberItem
              allowClear
              precision={1}
              max={maxWeight}
              disabled={disabled}
              onBlur={handleGrossWeightBlur}
              formItemLayout={formItemLayout}
              fieldName={`grossWeight[${index}]`}
              getFieldDecorator={getFieldDecorator}
              onInputChange={handleGrossWeightChange}
              label={intl.formatMessage({
                id: "consignment.services.adr.grossWeight",
                defaultMessage: "Celková váha",
              })}
              message={intl.formatMessage({
                id: "consignment.services.adr.emptyGrossWeight",
                defaultMessage: "Zadejte prosím celkovou váhu!",
              })}
              initialValue={initialAdr && initialAdr.grossWeight}
            />
          )}
          {(underLimitQuantity || fullAdrQuantity) && (
            <RadioGroupItem
              disabled={disabled}
              formItemLayout={formItemLayout}
              fieldName={`environmentalHazard[${index}]`}
              className={czech ? "services-form-two-line-label" : ""}
              label={intl.formatMessage({
                id: "consignment.services.adr.environmentalHazard",
                defaultMessage: "Nebezpečné pro životní prostředí",
              })}
              message={intl.formatMessage({
                id: "consignment.services.adr.environmentalHazard.empty",
                defaultMessage: "Vyberte prosím",
              })}
              labels={[
                intl.formatMessage({
                  id: "messages.yes",
                  defaultMessage: "Ano",
                }),
                intl.formatMessage({
                  id: "messages.no",
                  defaultMessage: "Ne",
                }),
              ]}
              getFieldDecorator={getFieldDecorator}
              initialValue={
                initialAdr && isBool(initialAdr.environmentalHazard)
                  ? initialAdr.environmentalHazard
                    ? 1
                    : 2
                  : undefined
              }
            />
          )}
        </Input.Group>
      </Col>
      <Col span={2}>
        <IconButton
          shape="none"
          icon="delete"
          disabled={count === 1}
          onClick={handleAdrDelete}
          title={intl.formatMessage({
            id: "consignment.services.adr.drop",
            defaultMessage: "Odebrat ADR kus",
          })}
        />
      </Col>
    </Row>
  );
};

export default AdrItem;
